import { useId, useState } from "react";
import { TickerBadge } from "@/components/site/FundBits";
import { money, pct, aum, fee, distAmt } from "@/lib/api";
import { t } from "@/lib/i18n";

const Stat = ({ label, value, sub, testid, className = "" }) => (
  <div className={`bg-white p-3 ${className}`}>
    <dt className="font-heading text-[10px] font-semibold uppercase tracking-wider text-hv-muted">{label}</dt>
    <dd className="mt-0.5 font-heading text-[15px] font-bold text-hv-navy" data-testid={testid}>{value}</dd>
    {sub && <dd className="text-[11px] leading-tight text-hv-muted">{sub}</dd>}
  </div>
);

export const FundCard = ({ card, lang }) => {
  const s = t(lang);
  const [idx, setIdx] = useState(0);
  const c = card.classes[idx];
  const base = card.base_ticker.toLowerCase();
  const tabsId = `fc-${base}-${useId().replace(/:/g, "")}`;
  return (
    <section className="hv-rise border border-hv-rule bg-white" aria-label={`${card.fund_name} fund card`} data-testid={`fund-card-${base}`}>
      <header className="flex items-start gap-3 border-b border-hv-rule p-3">
        <TickerBadge ticker={c.ticker} brand={card.brand} className="h-9" />
        <div className="min-w-0">
          <h3 className="font-heading text-[13px] font-bold leading-snug text-hv-text">{card.fund_name}</h3>
          <p className="text-[11px] text-hv-muted">TSX: {c.ticker} · {card.category}</p>
        </div>
      </header>
      {card.classes.length > 1 && (
        <div role="tablist" aria-label="Share class" className="flex border-b border-hv-rule px-3">
          {card.classes.map((cl, i) => (
            <button key={cl.ticker} role="tab" id={`${tabsId}-${i}`} aria-selected={i === idx} aria-controls={`${tabsId}-panel`}
              onClick={() => setIdx(i)} data-testid={`fund-card-class-tab-${cl.ticker.toLowerCase().replace(/\./g, "-")}`}
              onKeyDown={(e) => { if (e.key === "ArrowRight") setIdx((idx + 1) % card.classes.length); if (e.key === "ArrowLeft") setIdx((idx - 1 + card.classes.length) % card.classes.length); }}
              className={`-mb-px border-b-2 px-3 py-2 font-heading text-[11px] font-semibold uppercase tracking-wider ${i === idx ? "border-hv-navy text-hv-navy" : "border-transparent text-hv-muted hover:text-hv-navy"}`}>
              {cl.class === "Pref." ? "Pref." : `Class ${cl.class}`}
            </button>
          ))}
        </div>
      )}
      <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${idx}`}>
        <dl className="grid grid-cols-2 gap-px bg-hv-rule sm:grid-cols-3">
          <Stat label={s.price} value={money(c.market_price)} sub={s.endOfDay} testid="fund-card-price" />
          <Stat label={s.nav} value={money(c.nav)} />
          <Stat label={s.yield} value={c.current_yield_pct != null ? pct(c.current_yield_pct) : "—"} sub={c.current_yield_note ? "Not yet available" : null} testid="fund-card-yield" />
          <Stat label={s.lastDist} value={distAmt(c.last_distribution)} sub={c.distribution_frequency} testid="fund-card-distribution" />
          <Stat label={s.risk} value={c.risk_rating} testid="fund-card-risk" />
          <Stat label={s.fee} value={fee(c.mgmt_fee_pct)} testid="fund-card-fee" />
          <Stat label={s.aum} value={aum(c.aum_m)} className="col-span-2 sm:col-span-3" />
        </dl>
        {c.current_yield_note && <p className="border-t border-hv-rule px-3 py-2 text-[11px] text-hv-text">{c.current_yield_note}</p>}
        {card.leverage_note && <p className="border-t border-hv-rule px-3 py-2 text-[11px] text-hv-text" data-testid="fund-card-leverage-note"><strong className="text-hv-navy">Leverage:</strong> {card.leverage_note}</p>}
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-hv-rule p-3">
        <p className="text-[11px] text-hv-muted" data-testid="fund-card-as-at">{s.asAt} {c.as_at} · {s.endOfDay}</p>
        <div className="flex gap-2">
          <a href={c.page_url} target="_blank" rel="noreferrer" className="hv-btn-light hv-btn-sm" data-testid="fund-card-fund-page-button">{s.fundPage}</a>
          {card.documents?.etf_facts && <a href={card.documents.etf_facts} target="_blank" rel="noreferrer" className="hv-btn-light hv-btn-sm" data-testid="fund-card-etf-facts-button">{s.etfFacts}</a>}
        </div>
      </footer>
    </section>
  );
};
